/**
 * useMasterShellAuthority — signal CANONIQUE d'autorité plateforme, côté client.
 *
 * Lit `rectorium_team_members` (master_portal_role, status, expiration) par
 * e-mail de l'utilisateur authentifié. La table est protégée par la RLS
 * `platform_admin_only` (auth_is_platform_admin, durcie en C2) : un utilisateur
 * NON plateforme obtient ZÉRO ligne → `portalRoleId = null` → refus. Le serveur
 * reste la vraie barrière ; ce hook n'est qu'un miroir fail-closed pour éviter de
 * monter l'UID Master à un non-autorisé.
 *
 * Ne dérive JAMAIS l'autorité de profiles.role, tenant_id, un tableau frontend,
 * une URL ou le localStorage. Une erreur/ligne absente = défaut refusé.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../../services/supabase';

let cached; // undefined = jamais chargé ; sinon { portalRoleId, status, expiresAt }
const VIDE = { portalRoleId: null, status: null, expiresAt: null };

/**
 * Un refus n'est mis en cache que s'il est PROUVÉ : e-mail réel interrogé sans
 * erreur et ligne absente. Une session pas encore hydratée (e-mail null juste
 * après le retour OAuth) ou une erreur réseau rendent VIDE — fail-closed — mais
 * SANS cache, sinon le refus transitoire reste collé au niveau module et
 * verrouille un super légitime jusqu'au rechargement complet (incident
 * 2026-08-02, que le repli fail-open de 14c98d1 masquait au lieu de corriger).
 */
async function fetchAuthority() {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  const email = userData?.user?.email || null;
  if (userError || !email) return { autorite: { ...VIDE }, prouve: false };
  const { data, error } = await supabase
    .from('rectorium_team_members')
    .select('master_portal_role, status, master_role_expires_at')
    .eq('email', email)
    .maybeSingle();
  if (error) return { autorite: { ...VIDE }, prouve: false }; // fail-closed : jamais de faux verrou inversé
  if (!data) return { autorite: { ...VIDE }, prouve: true };  // non-membre avéré
  return {
    autorite: {
      portalRoleId: data.master_portal_role || null,
      status: data.status || null,
      expiresAt: data.master_role_expires_at || null,
    },
    prouve: true,
  };
}

/** Invalide le cache (logout / changement d'affectation). */
export function invalidateMasterShellAuthorityCache() { cached = undefined; }

export default function useMasterShellAuthority() {
  const [state, setState] = useState(() => (cached
    ? { ...cached, loaded: true }
    : { ...VIDE, loaded: false }));

  useEffect(() => {
    let alive = true;

    const charger = () => {
      fetchAuthority()
        .then(({ autorite, prouve }) => {
          if (prouve) cached = autorite;
          if (alive) setState({ ...autorite, loaded: true });
        })
        .catch(() => { if (alive) setState({ ...VIDE, loaded: true }); });
    };

    if (!cached) charger();

    // Tout changement d'état d'authentification (connexion, déconnexion,
    // rafraîchissement de jeton) invalide le cache et re-prouve l'autorité :
    // le signal suit toujours la session réelle, sans rechargement manuel.
    const { data: abonnement } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'INITIAL_SESSION') return; // déjà couvert par le chargement initial
      cached = undefined;
      charger();
    });

    return () => {
      alive = false;
      abonnement?.subscription?.unsubscribe?.();
    };
  }, []);

  return state;
}
